import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { X, Upload, Calendar, AlertCircle, Download } from 'lucide-react';
import { Vehicle } from '../../../types/saas';
import saasApiService from '../../../services/saasApi';

const schema = yup.object().shape({
  brand: yup.string().required('Marca requerida'),
  model: yup.string().required('Modelo requerido'),
  year: yup.number().min(1900, 'Año inválido').max(new Date().getFullYear() + 1, 'Año inválido').required('Año requerido'),
  license_plate: yup.string().required('Placa requerida'),
  engine_serial: yup.string().optional(),
  mileage: yup.number().min(0, 'Kilometraje debe ser positivo').required('Kilometraje requerido'),
  status: yup.string().oneOf(['active', 'maintenance', 'inactive']).required('Estado requerido'),
  fuel_type: yup.string().oneOf(['gasoline', 'diesel', 'electric', 'hybrid']).required('Tipo de combustible requerido'),
  capacity_tons: yup.number().min(0, 'Capacidad debe ser positiva').optional(),
  registration_expiry: yup.string().optional(), // TUCE
  insurance_expiry: yup.string().optional(),    // SOAT
  citv_expiry: yup.string().optional(),         // CITV
  notes: yup.string().optional(),
});

interface VehicleFormProps {
  vehicle?: Vehicle | null;
  onClose: () => void;
  onSave: () => void;
}

const VehicleForm: React.FC<VehicleFormProps> = ({ vehicle, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationFile, setRegistrationFile] = useState<File | null>(null); // TUCE
  const [insuranceFile, setInsuranceFile] = useState<File | null>(null);       // SOAT
  const [citvFile, setCitvFile] = useState<File | null>(null);                 // CITV
  const [tiveFile, setTiveFile] = useState<File | null>(null);                 // TIVE

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      brand: '',
      model: '',
      year: new Date().getFullYear(),
      license_plate: '',
      engine_serial: '',
      mileage: 0,
      status: 'active',
      fuel_type: 'gasoline',
      capacity_tons: '',
      registration_expiry: '', // TUCE
      insurance_expiry: '',    // SOAT
      citv_expiry: '',         // CITV
      notes: ''
    }
  });

  // Build absolute URL to backend static files
  const getFileUrl = (relativePath?: string) => {
    if (!relativePath) return '#';
    const apiBase = process.env.REACT_APP_SAAS_API_URL || 'http://localhost:5000/api';
    const backendBase = apiBase.replace(/\/?api\/?$/, '');
    const cleanPath = relativePath.replace(/^\//, '');
    return `${backendBase}/${cleanPath}`;
  };

  useEffect(() => {
    if (vehicle) {
      reset({
        brand: vehicle.brand,
        model: vehicle.model,
        year: vehicle.year,
        license_plate: vehicle.license_plate,
        engine_serial: vehicle.engine_serial || '',
        mileage: vehicle.mileage,
        status: vehicle.status,
        fuel_type: vehicle.fuel_type,
        capacity_tons: vehicle.capacity_tons ? vehicle.capacity_tons.toString() : '',
        registration_expiry: vehicle.registration_expiry ? vehicle.registration_expiry.split('T')[0] : '',
        insurance_expiry: vehicle.insurance_expiry ? vehicle.insurance_expiry.split('T')[0] : '',
        citv_expiry: (vehicle as any).citv_expiry ? (vehicle as any).citv_expiry.split('T')[0] : '',
        notes: vehicle.notes || ''
      });
    }
  }, [vehicle, reset]);

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      setError('');

      const formData = new FormData();
      
      // Agregar datos del formulario
      Object.keys(data).forEach(key => {
        if (data[key] !== '' && data[key] !== null && data[key] !== undefined) {
          formData.append(key, data[key]);
        }
      });

      // Agregar archivos si existen
      if (registrationFile) {
        formData.append('registration_document', registrationFile); // TUCE
      }
      if (insuranceFile) {
        formData.append('insurance_document', insuranceFile);       // SOAT
      }
      if (citvFile) {
        formData.append('citv_document', citvFile);                 // CITV
      }
      if (tiveFile) {
        formData.append('tive_document', tiveFile);                 // TIVE
      }

      if (vehicle) {
        await saasApiService.updateVehicle(vehicle.id, formData);
      } else {
        await saasApiService.createVehicle(formData);
      }

      onSave();
      onClose();
    } catch (error: any) {
      console.error('Error saving vehicle:', error);
      
      if (error.response?.status === 409) {
        setError('La placa ya está registrada. Por favor, usa una placa diferente.');
      } else if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || 'Datos inválidos';
        const validationErrors = error.response?.data?.errors;
        
        if (validationErrors && validationErrors.length > 0) {
          const errorMessages = validationErrors.map((err: any) => err.msg).join(', ');
          setError(`${errorMessage}: ${errorMessages}`);
        } else {
          setError(errorMessage);
        }
      } else {
        setError(error.response?.data?.message || 'Error al guardar el vehículo');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, type: 'registration' | 'insurance' | 'citv' | 'tive') => {
    const file = event.target.files?.[0];
    if (file) {
      if (type === 'registration') {
        setRegistrationFile(file);
      } else {
        if (type === 'insurance') setInsuranceFile(file);
        else if (type === 'citv') setCitvFile(file);
        else if (type === 'tive') setTiveFile(file);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            {vehicle ? 'Editar Vehículo' : 'Agregar Vehículo'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Información básica */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Marca *</label>
              <input
                {...register('brand')}
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Toyota, Ford, etc."
              />
              {errors.brand && <p className="mt-1 text-sm text-red-600">{errors.brand.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Modelo *</label>
              <input
                {...register('model')}
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Hilux, F-150, etc."
              />
              {errors.model && <p className="mt-1 text-sm text-red-600">{errors.model.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Año *</label>
              <input
                {...register('year')}
                type="number"
                min="1900"
                max={new Date().getFullYear() + 1}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              {errors.year && <p className="mt-1 text-sm text-red-600">{errors.year.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Placa *</label>
              <input
                {...register('license_plate')}
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="ABC-123"
              />
              {errors.license_plate && <p className="mt-1 text-sm text-red-600">{errors.license_plate.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Número de Motor</label>
              <input
                {...register('engine_serial')}
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Kilometraje *</label>
              <input
                {...register('mileage')}
                type="number"
                min="0"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              {errors.mileage && <p className="mt-1 text-sm text-red-600">{errors.mileage.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Estado *</label>
              <select
                {...register('status')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="active">Activo</option>
                <option value="maintenance">Mantenimiento</option>
                <option value="inactive">Inactivo</option>
              </select>
              {errors.status && <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Tipo de Combustible *</label>
              <select
                {...register('fuel_type')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="gasoline">Gasolina</option>
                <option value="diesel">Diésel</option>
                <option value="electric">Eléctrico</option>
                <option value="hybrid">Híbrido</option>
              </select>
              {errors.fuel_type && <p className="mt-1 text-sm text-red-600">{errors.fuel_type.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Capacidad (Toneladas)</label>
              <input
                {...register('capacity_tons')}
                type="number"
                min="0"
                step="0.1"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Fechas de vencimiento (SOAT, TUCE, CITV) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                <Calendar className="inline h-4 w-4 mr-1" />
                Vencimiento TUCE
              </label>
              <input
                {...register('registration_expiry')}
                type="date"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                <Calendar className="inline h-4 w-4 mr-1" />
                Vencimiento SOAT
              </label>
              <input
                {...register('insurance_expiry')}
                type="date"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                <Calendar className="inline h-4 w-4 mr-1" />
                Vencimiento CITV
              </label>
              <input
                {...register('citv_expiry')}
                type="date"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Documentos: TUCE, SOAT, CITV, TIVE */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">TUCE (Tarjeta Única de Circulación)</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                      <span>Subir archivo</span>
                      <input
                        type="file"
                        className="sr-only"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange(e, 'registration')}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">PDF, PNG, JPG hasta 5MB</p>
                  {registrationFile && (
                    <p className="text-sm text-green-600">{registrationFile.name}</p>
                  )}
                  {!registrationFile && vehicle?.registration_document && (
                    <a href={getFileUrl(vehicle.registration_document)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm">
                      <Download className="h-4 w-4 mr-1" /> Descargar TUCE
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">SOAT</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                      <span>Subir archivo</span>
                      <input
                        type="file"
                        className="sr-only"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange(e, 'insurance')}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">PDF, PNG, JPG hasta 5MB</p>
                  {insuranceFile && (
                    <p className="text-sm text-green-600">{insuranceFile.name}</p>
                  )}
                  {!insuranceFile && vehicle?.insurance_document && (
                    <a href={getFileUrl(vehicle.insurance_document)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm">
                      <Download className="h-4 w-4 mr-1" /> Descargar SOAT
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Más documentos: CITV y TIVE */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">CITV</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                      <span>Subir archivo</span>
                      <input
                        type="file"
                        className="sr-only"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange(e, 'citv')}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">PDF, PNG, JPG hasta 5MB</p>
                  {citvFile && (
                    <p className="text-sm text-green-600">{citvFile.name}</p>
                  )}
                  {!citvFile && (vehicle as any)?.citv_document && (
                    <a href={getFileUrl((vehicle as any).citv_document)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm">
                      <Download className="h-4 w-4 mr-1" /> Descargar CITV
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">TIVE (sin fecha)</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                      <span>Subir archivo</span>
                      <input
                        type="file"
                        className="sr-only"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange(e, 'tive')}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">PDF, PNG, JPG hasta 5MB</p>
                  {tiveFile && (
                    <p className="text-sm text-green-600">{tiveFile.name}</p>
                  )}
                  {!tiveFile && (vehicle as any)?.tive_document && (
                    <a href={getFileUrl((vehicle as any).tive_document)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm">
                      <Download className="h-4 w-4 mr-1" /> Descargar TIVE
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* Notas */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Notas</label>
            <textarea
              {...register('notes')}
              rows={3}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Información adicional sobre el vehículo..."
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </div>
              ) : (
                vehicle ? 'Actualizar' : 'Crear'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehicleForm;
